import { useCurrentUser } from '../use-current-user'
import useSWR from 'swr'
import type { WorkspaceSubscription } from '@/lib/types'

interface WorkspaceSubscriptionsResponse {
  owned: WorkspaceSubscription[]
  member: WorkspaceSubscription[]
  allSubscriptions: WorkspaceSubscription[]
}

export function useWorkspaceSubscription(workspaceId?: string | null) {
  const { data: user } = useCurrentUser()

  const {
    data: response,
    error,
    mutate,
  } = useSWR<WorkspaceSubscriptionsResponse>(
    user ? '/api/me/workspace-subscriptions' : null,
    (url: string) => fetch(url).then((res) => res.json()),
  )

  // Handle both old array format and new grouped format for backward compatibility
  const subscriptions = Array.isArray(response)
    ? response
    : (response?.allSubscriptions ?? [])

  // Ensure subscriptions is always an array
  const validSubscriptions = Array.isArray(subscriptions) ? subscriptions : []

  // Owned subscriptions (can upgrade/manage)
  const ownedSubscriptions = Array.isArray(response)
    ? validSubscriptions // Old format - assume all are owned
    : (response?.owned ?? [])

  // Member subscriptions (read-only feature access)
  const memberSubscriptions = Array.isArray(response)
    ? [] // Old format - no member subscriptions
    : (response?.member ?? [])

  // Filter subscriptions by workspace if workspaceId is provided
  const filteredSubscriptions = workspaceId
    ? validSubscriptions.filter((s) => s.workspace_id === workspaceId)
    : validSubscriptions

  // Include past_due - users keep access during grace period while Stripe retries payment
  // Exclude unpaid - all retries exhausted, access revoked until payment updated
  const activeSubscription = filteredSubscriptions.find(
    (s) => s.status === 'active' || s.status === 'past_due',
  )

  // Find subscription with payment issues (for banner display even if not active)
  const subscriptionWithPaymentIssue = filteredSubscriptions.find(
    (s) => s.status === 'past_due' || s.status === 'unpaid',
  )

  // Find incomplete subscription (payment not completed or payment window expired)
  const incompleteSubscription = filteredSubscriptions.find(
    (s) => s.status === 'incomplete' || s.status === 'incomplete_expired',
  )

  const subscription = {
    loading: !response && !error,
    error,
    allSubscriptions: validSubscriptions,
    ownedSubscriptions,
    memberSubscriptions,
    activeSubscription,
    incompleteSubscription,
    subscriptionWithPaymentIssue,
    mutate,
  }
  // console.log('subscription:', subscription)
  return subscription
}
